import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Spinner, Table } from "react-bootstrap";

import Loader from "@/components/Loader";
import ErrorComponent from "@/components/ErrorComponent";
import { translate } from "@/components/IntlProviderWrapper";
import { isInRoles, ROLE_ADMIN, ROLE_POWER_USER } from "@/lib/Auth";
import { classNames, niceDateTime } from "@/lib/Util";
import { getBackend as getBackend2 } from "@/lib/backend/Backend2";
import type { IGetLatestUsageKpiReportRes } from "@/lib/backend/reports.generated.types";
import { useJobStatusPolling } from "@/lib/custom-hooks/useJobStatusPolling";
import { mapReportToViewModel, type TimeRange, type TimeRangeKey, type ViewModel } from "@/lib/UsageKPIs";

import "./UsageKPIs.scss";

const usageKpiTimeRangeTranslationId = (timeRangeKey: TimeRangeKey) => `Admin.UsageKPIs.time_range.${timeRangeKey}`;

/** Renders the cards for a time range metric in the organization summary. */
const renderCards = (points: TimeRange[]) => (
    <div className="custom-grid gap-3" role="group">
        {points.map(point => (
            <div key={point.timeRangeKey} className="card">
                <div className="d-flex flex-column justify-content-between gap-3 height-100">
                    <div className="card-title card-title-small card-title-light card-title-all-caps">
                        {translate(usageKpiTimeRangeTranslationId(point.timeRangeKey))}
                    </div>
                    <div className="card-value card-value--big">{point.value}</div>
                </div>
            </div>
        ))}
    </div>
);

/** Renders the cells for a metric in the plants table. groupByType is used to group the cells by the type (visits, visitors). */
const renderPlantsTableCells = (
    points: TimeRange[],
    {
        groupStart = false,
    }: {
        groupStart?: boolean;
    } = {}
) =>
    points.map((point, index) => (
        <td
            key={point.timeRangeKey}
            className={classNames("text-right", {
                "table-col-group-start": groupStart && index === 0,
            })}
        >
            {point.value}
        </td>
    ));

/** Renders a table displaying usage KPIs for each plant. Plants are grouped by the type (visits, visitors) and date. */
const renderPlantsTable = (viewModel: ViewModel) => {
    const { timeRangeKeys, plants } = viewModel;
    const periodCount = timeRangeKeys.length;

    return (
        <div className="card p-0 border-top-no-radius">
            <div className="card-content">
                <div className="table-responsive">
                    <Table className="dashboard-table mb-0">
                        <thead className="table-header-neutral">
                            <tr>
                                <th rowSpan={2} scope="col" className="align-middle">
                                    <div className="card-title">{translate("common.plants", "Plants")}</div>
                                </th>
                                <th colSpan={periodCount} scope="colgroup" className="text-center">
                                    {translate("Admin.UsageKPIs.section.visits", "Visits")}
                                </th>
                                <th
                                    colSpan={periodCount}
                                    scope="colgroup"
                                    className="text-center table-col-group-start"
                                >
                                    {translate("Admin.UsageKPIs.section.visitors", "Visitors")}
                                </th>
                            </tr>
                            <tr>
                                {timeRangeKeys.map(key => (
                                    <th key={`visits-${key}`} scope="col" className="text-right">
                                        {translate(usageKpiTimeRangeTranslationId(key))}
                                    </th>
                                ))}
                                {timeRangeKeys.map((key, index) => (
                                    <th
                                        key={`visitors-${key}`}
                                        scope="col"
                                        className={classNames("text-right", {
                                            "table-col-group-start": index === 0,
                                        })}
                                    >
                                        {translate(usageKpiTimeRangeTranslationId(key))}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {plants.length === 0 ? (
                                <tr>
                                    <td colSpan={1 + periodCount * 2} className="text-muted">
                                        {translate("common.no_data")}
                                    </td>
                                </tr>
                            ) : (
                                plants.map(plant => (
                                    <tr key={plant.uuid}>
                                        <td>
                                            <Link
                                                to={`/digital-twin/resources/plants/${plant.uuid}`}
                                                key={plant.uuid}
                                                className="dash-link"
                                            >
                                                {plant.title}
                                            </Link>
                                            <span className="text-muted ml-2">{plant.external_id}</span>
                                        </td>
                                        {renderPlantsTableCells(plant.kpis.visits)}
                                        {renderPlantsTableCells(plant.kpis.visitors, { groupStart: true })}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>
        </div>
    );
};

export default function UsageKPIs() {
    const [report, setReport] = useState<IGetLatestUsageKpiReportRes | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [rerunStartError, setRerunStartError] = useState<string | null>(null);

    const loadCancelledRef = useRef(false);

    /**
     * Loads the usage KPI report.
     * @param soft - If true, only sets the error state to null and does not set the loading state to true.
     */
    const load = useCallback(async (soft = false) => {
        if (!soft) {
            setLoading(true);
            setError(null);
            setReport(null);
        } else {
            setError(null);
        }
        try {
            const response = await getBackend2().reports.getLatestUsageKpiReport({});
            if (!loadCancelledRef.current) {
                setReport(response);
            }
        } catch (err: unknown) {
            if (!loadCancelledRef.current) {
                console.error((err as Error).message);
                setError(translate("common.error"));
            }
        } finally {
            if (!loadCancelledRef.current && !soft) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        loadCancelledRef.current = false;
        void load();
        return () => {
            loadCancelledRef.current = true;
        };
    }, [load]);

    const {
        startPolling,
        isPolling,
        error: pollError,
    } = useJobStatusPolling({
        onCompleted: () => load(true),
    });

    const rerunError = rerunStartError ?? pollError;

    useEffect(() => {
        if (rerunError) {
            console.error(rerunError);
        }
    }, [rerunError]);

    const handleRerun = useCallback(async () => {
        setRerunStartError(null);
        try {
            const { token } = await getBackend2().reports.runUsageKpiReport({});
            startPolling(token);
        } catch (e: unknown) {
            setRerunStartError((e as Error).message);
        }
    }, [startPolling]);

    const viewModel = report ? mapReportToViewModel(report) : null;

    const renderRerunButton = useCallback(
        (showButton: boolean) => (
            <div className="d-flex flex-column justify-content-center align-items-end gap-2">
                {showButton && (
                    <>
                        {rerunError && (
                            <Badge variant="danger">
                                <i className="fas fa-exclamation-triangle"></i> {translate("common.failed")}
                            </Badge>
                        )}
                        <Button
                            variant="btn"
                            className="rerun-btn d-flex align-items-center"
                            onClick={() => void handleRerun()}
                            disabled={isPolling && !rerunError}
                        >
                            {isPolling && !rerunError && <Spinner animation="border" size="sm" className="mr-2" />}
                            {translate("Admin.UsageKPIs.rerunKpiCalculation")}
                        </Button>
                    </>
                )}
                {viewModel?.createdAt && (
                    <span className="align-self-end text-right text-smaller text-muted">
                        {translate("common.last_update")}{" "}
                        <span className={isPolling ? "" : "font-weight-bold"}>
                            {niceDateTime(new Date(viewModel.createdAt))}
                        </span>
                    </span>
                )}
            </div>
        ),
        [viewModel, handleRerun, isPolling, rerunError]
    );

    const renderContent = (viewModel: ViewModel) => (
        <div className="d-flex flex-column gap-4">
            <div className="d-flex flex-column gap-1">
                <section>
                    <h3 className="content-subsubtitle">{translate("Admin.UsageKPIs.section.visits", "Visits")}</h3>
                    {renderCards(viewModel.org.kpis.visits)}
                </section>
                <hr className="m-0 mt-3 mb-2" />
                <section>
                    <h3 className="content-subsubtitle">{translate("Admin.UsageKPIs.section.visitors", "Visitors")}</h3>
                    {renderCards(viewModel.org.kpis.visitors)}
                </section>
            </div>
        </div>
    );

    return (
        <section className="usage-kpis-page d-flex flex-column gap-2">
            <div className="usage-kpi-header d-flex justify-content-between align-items-end">
                <div>
                    <h2 className="content-subtitle mb-2">{translate("Header.menu.usage_kpis", "Usage KPIs")}</h2>
                    {viewModel && (
                        <table className="data-fields d-flex gap-2">
                            <tbody>
                                <tr>
                                    <th>{translate("common.organization", "Organization")}</th>
                                    <td>
                                        <Link to={`/digital-twin/resources/organisation`} className="dash-link">
                                            {viewModel.org.title}
                                        </Link>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </div>
                {renderRerunButton(isInRoles([ROLE_ADMIN, ROLE_POWER_USER]))}
            </div>
            <div>
                <div className="main-section main-section--rounded border-bottom-no-radius border-bottom-0 pb-4">
                    {error && <ErrorComponent msg={error} type="error" />}
                    {loading && <Loader small={true}></Loader>}
                    {viewModel && <div className="d-flex flex-column gap-3">{renderContent(viewModel)}</div>}
                </div>
                {viewModel && renderPlantsTable(viewModel)}
            </div>
        </section>
    );
}
